/**
 * /dashboard/coc/clinic/:id -- the COC's per-clinic drill (J4 "My clinics"
 * drill; 04-SURFACE-FATES row 34 KEEP).
 *
 * TRAIN-B-CLINIC-DAY-HANDOFF-V1: the scaffold note becomes the live clinic
 * DAY BOARD, VIEW-ONLY (APPENDIX A / ACCESS-RIGHTS-RBAC-2026-06-12: COC ops
 * monitoring - check-in/check-out status + the live day queue - for GRANTED
 * clinics; NO clinical modification). One computation: the SAME
 * GET /api/appointments/today?view=board read the /walkin Today home uses,
 * pinned to the route's clinic id. The backend fences the id to the
 * caller's scope.clinicIds (an ungranted clinic -> 401 -> the honest error
 * state here, never data). The board's role lens renders NO actions for
 * coc (SEE-NOT-DO; recorded ruling D-5) and the server ships no clinical
 * field to a non-doctor role.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchTodayAppointments } from '@/dashboard/api';
import ClinicDayBoard from '@/dashboard/appointments/ClinicDayBoard';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { ErrorState, PageHeader } from '@/dashboard/ui';
import type { TodayAppointment } from '@/dashboard/types';

type BoardState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: TodayAppointment[] }
  | { kind: 'error' };

export default function ClinicStatusPage() {
  const { id } = useParams();
  const meCtx = useDashboardMeContext();
  const me = meCtx?.me ?? null;
  const clinicId = /^\d+$/.test(id ?? '') ? Number(id) : null;
  const [board, setBoard] = useState<BoardState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (clinicId === null || me === null) {
      setBoard({ kind: 'error' });
      return;
    }
    let cancelled = false;
    setBoard({ kind: 'loading' });
    fetchTodayAppointments(clinicId, 'board')
      .then((data) => {
        if (!cancelled) setBoard({ kind: 'ready', rows: [...data.appointments] });
      })
      .catch(() => {
        // Out-of-scope clinic (not granted) or a failed read: the honest
        // error state -- never invented rows, never a cross-clinic leak.
        if (!cancelled) setBoard({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, me, reloadKey]);

  return (
    <div className="clinic-status">
      {/* UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2): a deep surface
          reached from the COC home. The breadcrumb gives the lawful back-path
          to My clinics (no dead end), demonstrating the shared trail grammar.
          The crumb points only where the COC already navigates from. */}
      <PageHeader
        breadcrumbs={[
          { label: 'My clinics', to: '/dashboard/coc' },
          { label: 'Clinic status' },
        ]}
        title="Clinic status"
        subs={[`Clinic ID: ${clinicId ?? '-'}`]}
      />
      {board.kind === 'error' ? (
        // V7-P4 adoption: the shared ErrorState (03 P11 S4), copy unchanged.
        <ErrorState
          title="This clinic's day could not load."
          detail="It may not be in your granted clinics, or the read failed - refresh to retry."
        />
      ) : (
        <ClinicDayBoard
          rows={board.kind === 'ready' ? board.rows : []}
          role={me?.role ?? null}
          loading={board.kind === 'loading'}
          error={false}
          onChanged={() => setReloadKey((k) => k + 1)}
          // SEP15 vertical: the Patient 360 continuity door on linked rows
          // (role-shaped in the board; reachability from the /me capability
          // signal -- fail-closed, LES-016: never a dead door).
          patientRecordDoor={{ enabled: me?.patientMasterEnabled === true }}
        />
      )}
    </div>
  );
}
